import React, { useRef, useState, useEffect } from "react";
import LogoIcon from "../../../../assets/icons/logo-icon.svg";
import CustomButton from "../../../../global/components/CustomButton/CustomButton";
import VisibilityIcon from "../../../../assets/icons/VisibilityIcon.svg";
import VisibilityOffIcon from "../../../../assets/icons/VisibilityOff.svg";
import history from "../../../../utils/history";
import urls from "../../../../global/constants/UrlConstants";
import { loginValidation } from "./SignTypesAndValidation";
import { login } from "../../landingPageService";
import { loginAction } from "../../../../redux/authSlice";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import notifiers from "../../../../global/constants/NotificationConstants";
import { useAppDispatch } from "../../../../utils/hooks";
import customErrorHandler from "../../../../utils/customErrorHandler";
import CustomLoader from "../../../../global/components/CustomLoader/CustomLoader";
import { AnimatePresence, motion } from "framer-motion";
import { patternCheckForMobile } from "./SignService";
import { YhAuthShell } from "../../../../ui/YhAuthShell";

/** React 19-compatible motion elements (framer-motion DOM prop types) */
const MotionDiv = motion.div as React.ComponentType<
  React.HTMLAttributes<HTMLDivElement> & Record<string, unknown>
>;
const MotionSpan = motion.span as React.ComponentType<
  React.HTMLAttributes<HTMLSpanElement> & Record<string, unknown>
>;

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 018 0v3" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const FeatureItem = ({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) => (
  <div className="flex items-start gap-3.5">
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-white shadow-[0_2px_8px_rgba(112,71,235,0.1)] ring-1 ring-[#F9F0F4]">
      {icon}
    </div>
    <div className="min-w-0 pt-1">
      <p className="text-[15px] font-semibold text-[#1F2937]">{title}</p>
      <p className="mt-0.5 text-sm leading-relaxed text-[#6B7280]">{desc}</p>
    </div>
  </div>
);

const SignIn = ({ formFields, setFormFields }: any) => {
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState(true);
  const [isMatchEmailOrPhone, setIsMatchEmailOrPhone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [mobilePattern, setMobilePattern] = useState(false);
  const passwordInputRef: any = useRef<HTMLInputElement>(null);

  const handleOnChangeInputField = (event: React.ChangeEvent<any>) => {
    let name = event.target.name;
    let value = event.target.value;
    if (
      name === "email" &&
      event.target.value.length > 10 &&
      /^\d{11}$/.test(value)
    )
      return;
    if (name === "email") value = value.toLowerCase();
    setFormFields({
      ...formFields,
      [event.target.name]: {
        ...formFields[name],
        value: value,
        error: "",
      },
    });
    if (name === "email") {
      setIsEmailVerified(false);
      setIsMatchEmailOrPhone(false);
      let isEmail = patternCheckForMobile(value);
      setMobilePattern(isEmail);
    }
  };

  const handleValidation = () => {
    const { isValid, errors } = loginValidation(formFields);
    setFormFields({ ...errors });
    return isValid;
  };

  const handleClickShowPassword = () => {
    setShowPassword(showPassword);
  };

  const handleKeypress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const inputs = document.getElementsByTagName("input");
      const currentIndex = Array.from(inputs).indexOf(
        event.target as HTMLInputElement
      );
      const nextIndex = currentIndex + 1;

      if (nextIndex < inputs.length) {
        inputs[nextIndex].focus();
      } else {
        handleNextButton();
      }
    }
  };

  const handleMouseDownNewPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setShowPassword(!showPassword);
    event.preventDefault();
  };

  const handleSignUp = () => { };

  const handleforgotPassword = () => {
    history.push(urls.forgotPasswordViewPath);
  };

  const validateInputType = (inputValue: string) => {
    const mobilePattern = /^\d{10}$/;
    const emailPattern = /^[\w\.-]+@[\w\.-]+\.\w+$/;

    if (mobilePattern.test(inputValue)) {
      return false;
    } else if (emailPattern.test(inputValue)) {
      return false;
    } else {
      return true;
    }
  };

  const handleNextButton = async () => {
    let { email, password } = formFields;
    try {
      setIsLoading(true);
      const response = await login(email.value, password.value);

      if (
        !isEmailVerified &&
        !response?.message?.settings?.success &&
        !/^\d{10}$/.test(formFields.email.value)
      ) {
        const apiMsg =
          response?.message?.settings?.message ||
          "This is not a valid email. Please contact system administrator.";
        setFormFields({
          ...formFields,
          ["email"]: {
            ...formFields["email"],
            error: apiMsg,
          },
        });
        setIsLoading(false);
        return;
      } else if (
        response?.message?.settings?.message?.toLowerCase() !== "email found" &&
        !response?.message?.settings?.success
      ) {
        customErrorHandler(response);
      } else if (response?.message?.settings?.success && !isEmailVerified) {
        setIsEmailVerified(true);
        setIsMatchEmailOrPhone(true);
        setIsLoading(false);
        setFormFields({
          ...formFields,
          ["email"]: {
            ...formFields["email"],
            error: "",
          },
        });
        return;
      }
      if (
        !formFields.password.value ||
        (!response?.message?.settings?.success && mobilePattern) ||
        (!response?.message?.settings?.success && !mobilePattern)
      ) {
        setFormFields({
          ...formFields,
          ["password"]: {
            ...formFields["password"],
            error: "Please enter correct password",
          },
        });
        setIsLoading(false);
        return;
      }
      if (/^\d{10}$/.test(formFields.email.value)) {
        const otpMsg = response?.message?.settings?.message;
        openSuccessNotification(
          typeof otpMsg === "string" && otpMsg.trim()
            ? otpMsg
            : "OTP sent successfully"
        );
        history.push({
          pathname: urls.mobileVerificationViewPath,
          state: { userId: response.message.userId },
        });
        return;
      }
      const {
        accessToken,
        fullName,
        role,
        hierarchyLevel,
        _id,
        lastUpdate,
        isFirstLogin,
        uuid,
        isSuperAdmin,
        isAdmin,
        activeOrganizationId,
        requiresOrgSelection,
        memberships,
      } = response?.message?.data?.user;
      let privileges = response?.message?.data?.user?.role_privileges || {};
      if (typeof privileges === "string") {
        try {
          privileges = JSON.parse(privileges);
        } catch {
          privileges = {};
        }
      }
      dispatch(
        loginAction({
          authenticated: true,
          accessToken: accessToken,
          userName: fullName,
          email: response.message.data.user.email,
          role,
          hierarchyLevel,
          userId: _id,
          lastUpdate,
          privileges,
          isFirstLogin,
          uuid,
          isSuperAdmin: !!isSuperAdmin,
          isAdmin: !!isAdmin,
          activeOrganizationId: activeOrganizationId || null,
          requiresOrgSelection: !!requiresOrgSelection,
          memberships: memberships || [],
        })
      );
      setIsLoading(false);
      const rawMsg = response?.message?.settings?.message;
      const successMsg =
        typeof rawMsg === "string" && rawMsg.trim()
          ? rawMsg
          : "Logged in successfully";
      openSuccessNotification(successMsg);
    } catch (error: any) {
      setIsLoading(false);
      if (customErrorHandler({}, error) === false) return;
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  useEffect(() => {
    if (isEmailVerified || passwordInputRef.current) {
      passwordInputRef.current && passwordInputRef.current.focus();
    }
  }, [isEmailVerified, passwordInputRef.current]);

  const handleKeyDowns = (e: any) => {
    if (e?.key === "Enter" && !validateInputType(formFields.email.value)) {
      handleNextButton();
    }
  };

  void handleValidation;
  void handleSignUp;
  void isMatchEmailOrPhone;

  const mockupSrc = "/assets/image/dashboard_mockup_crop.png";

  const leftPanel = (
    <>
      <img
        src="/assets/image/auth-waves.svg"
        alt=""
        className="yh-auth-waves"
        aria-hidden
      />
      <div className="pointer-events-none absolute -right-20 top-8 h-64 w-64 rounded-full bg-[#DDD6FE]/40 blur-3xl" />
      <div className="pointer-events-none absolute bottom-28 left-6 h-44 w-44 rounded-full bg-[#C4B5FD]/25 blur-3xl" />

      <div className="relative z-[1] flex h-full min-h-0 flex-col overflow-hidden px-8 pt-8 sm:px-10 lg:px-12 lg:pt-10 xl:px-14 xl:pt-12 2xl:px-16">
        <div className="flex shrink-0 items-center gap-3">
          <img src={LogoIcon} alt="Pulse CRM" className="h-9 w-9" />
          <span className="text-lg font-bold tracking-tight text-[#111827]">
            Pulse CRM
          </span>
        </div>

        <div className="mt-6 max-w-xl shrink-0 lg:mt-8 xl:mt-9">
          <h1 className="text-[30px] font-bold leading-[1.12] tracking-tight text-[#111827] lg:text-[36px] xl:text-[42px]">
            Welcome back!
          </h1>
          <p className="mt-1.5 text-[22px] font-bold leading-snug text-[#111827] lg:text-[26px] xl:text-[30px]">
            Let&apos;s{" "}
            <span className="bg-gradient-to-r from-[#841C39] to-[#841C39] bg-clip-text text-transparent">
              grow your business
            </span>{" "}
            🚀
          </p>
          <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-[#6B7280]">
            Sign in to access your dashboard, manage relationships, and grow
            your business.
          </p>
        </div>

        <div className="mt-6 flex max-w-lg shrink-0 flex-col gap-4 lg:mt-7 xl:gap-5">
          <FeatureItem
            title="Manage Relationships"
            desc="Keep all your leads and clients organized."
            icon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#841C39" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
              </svg>
            }
          />
          <FeatureItem
            title="Track Performance"
            desc="Monitor your sales and team performance."
            icon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A02448" strokeWidth="2">
                <path d="M3 17l6-6 4 4 7-8" />
                <path d="M14 7h6v6" />
              </svg>
            }
          />
          <FeatureItem
            title="Secure & Reliable"
            desc="Your data is safe with enterprise security."
            icon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A02448" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            }
          />
        </div>

        <div className="yh-auth-mockup-wrap">
          <div className="yh-auth-mockup">
            <span className="yh-auth-sphere yh-auth-sphere-lg" />
            <span className="yh-auth-sphere yh-auth-sphere-md" />
            <span className="yh-auth-sphere yh-auth-sphere-sm" />
            <img src={mockupSrc} alt="Pulse CRM dashboard" />
          </div>
        </div>
      </div>
    </>
  );

  const rightPanel = (
    <MotionDiv
      className="yh-auth-form"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.35 } }}
    >
      <div className="mb-8 flex flex-col items-center text-center">
        <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F3F0FF] shadow-sm ring-1 ring-[#F9F0F4]">
          <img src={LogoIcon} alt="Pulse CRM" className="h-9 w-9" />
        </div>
        <h2 className="text-[22px] font-bold tracking-tight text-[#111827] xl:text-2xl">
          Sign in to <span className="text-[#841C39]">Pulse CRM</span>
        </h2>
        {/* <p className="mt-2 text-[15px] text-[#6B7280]">
          {isEmailVerified
            ? "Enter your password to sign in"
            : "Enter your email or mobile to continue"}
        </p> */}
      </div>

      <div className="flex flex-col gap-5">
        <div>
          <label
            htmlFor="email"
            className="mb-2 block text-[15px] font-semibold text-[#111827]"
          >
            Email
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 z-[1] -translate-y-1/2">
              {mobilePattern ? (
                <AnimatePresence>
                  <MotionSpan
                    key="cc"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="border-r border-[#E5E7EB] pr-2 text-sm text-[#374151]"
                  >
                    +91
                  </MotionSpan>
                </AnimatePresence>
              ) : (
                <MailIcon />
              )}
            </span>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="off"
              placeholder="Enter your email"
              value={formFields.email.value}
              onChange={handleOnChangeInputField}
              onKeyPress={handleKeypress}
              disabled={isEmailVerified}
              className={`yh-auth-input ${formFields.email.error ? "yh-auth-input-error" : ""
                } ${mobilePattern ? "!pl-16" : ""} ${isEmailVerified ? "bg-[#F9FAFB]" : ""
                }`}
            />
          </div>
          {formFields.email.error && (
            <p className="mt-1.5 text-xs text-red-500">{formFields.email.error}</p>
          )}
          {/* {isEmailVerified && (
            <button
              type="button"
              className="mt-2 text-sm font-medium text-[#841C39] hover:underline"
              onClick={() => {
                setIsEmailVerified(false);
                setIsMatchEmailOrPhone(false);
                setFormFields({
                  ...formFields,
                  password: { ...formFields.password, value: "", error: "" },
                });
              }}
            >
              Change email / mobile
            </button>
          )} */}
        </div>

        <AnimatePresence>
          {isEmailVerified && (
            <MotionDiv
              key="password-step"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div>
                <label
                  htmlFor="password"
                  className="mb-2 block text-[15px] font-semibold text-[#111827]"
                >
                  Password
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 z-[1] -translate-y-1/2">
                    <LockIcon />
                  </span>
                  <input
                    ref={passwordInputRef}
                    id="password"
                    name="password"
                    type={showPassword ? "password" : "text"}
                    autoComplete="off"
                    placeholder="Enter your password"
                    value={formFields.password.value}
                    onChange={handleOnChangeInputField}
                    onKeyPress={handleKeypress}
                    className={`yh-auth-input yh-auth-input-password ${formFields.password.error ? "yh-auth-input-error" : ""
                      }`}
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-0.5"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownNewPassword}
                  >
                    <img
                      src={showPassword ? VisibilityOffIcon : VisibilityIcon}
                      className="h-5 w-5"
                      alt="Toggle password"
                    />
                  </button>
                </div>
                {formFields.password.error && (
                  <p className="mt-1.5 text-xs text-red-500">
                    {formFields.password.error}
                  </p>
                )}
                {/* <div className="mt-2.5 flex justify-end">
                  <button
                    type="button"
                    onClick={handleforgotPassword}
                    className="text-[15px] font-medium text-[#841C39] hover:underline"
                  >
                    Forgot password?
                  </button>
                </div> */}
              </div>
            </MotionDiv>
          )}
        </AnimatePresence>

        <CustomButton
          label={isEmailVerified ? "Sign in" : "Continue"}
          variant="primary"
          fullWidth
          endIcon={<ArrowIcon />}
          onClick={() => handleNextButton()}
          disabled={
            !formFields.email.value ||
            validateInputType(formFields.email.value) ||
            (isEmailVerified && !formFields.password.value)
          }
          className="yh-auth-btn !mt-1 !justify-between !bg-gradient-to-r !from-[#841C39] !to-[#6B1630] !text-base hover:!from-[#6B1630] hover:!to-[#14028C]"
        />
      </div>

      <div className="my-7 flex items-center gap-3">
        <div className="h-px flex-1 bg-[#E5E7EB]" />
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#F3F4F6] text-[#9CA3AF]">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </span>
        <div className="h-px flex-1 bg-[#E5E7EB]" />
      </div>

      {/* <p className="text-center text-[15px] text-[#6B7280]">
        Don&apos;t have an account?{" "}
        <button
          type="button"
          className="font-semibold text-[#841C39] hover:underline"
          onClick={() => history.push(urls.signUpViewPath)}
        >
          Sign up
        </button>
      </p> */}

      <p className="mt-10 flex items-center justify-center gap-1.5 text-xs text-[#9CA3AF]">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 018 0v3" />
        </svg>
        Your data is secure and encrypted.
      </p>
    </MotionDiv>
  );

  return (
    <div onKeyDown={handleKeyDowns}>
      <YhAuthShell>
        <div className="yh-auth-row">
          <div className="yh-auth-col yh-auth-col-left">{leftPanel}</div>
          <div className="yh-auth-col yh-auth-col-right">{rightPanel}</div>
        </div>
      </YhAuthShell>
      {isLoading && <CustomLoader apiResponse={isLoading} />}
    </div>
  );
};

export default SignIn;
